import { useRef, useState } from "react";
import { AsYouType } from "libphonenumber-js";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { sampleContactPayload } from "@/lib/sampleContact";

const INITIAL_FORM = { name: "", email: "", phone: "", country: "US", address1: "", address2: "", city: "", region: "", postal_code: "", wants_extra: false, additional_bottles: 0, sharing_with: [], sharing_note: "" };

export const useInviteForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(null);
  const sending = useRef(false);

  const update = event => {
    const { name, value } = event.target;
    setForm(current => {
      if (name === "phone") {
        const prefix = value.trimStart().startsWith("+") ? "+" : "";
        const digits = value.replace(/\D/g, "").slice(0, 15);
        return { ...current, phone: digits ? new AsYouType(prefix ? undefined : current.country).input(prefix + digits) : prefix };
      }
      return { ...current, [name]: value };
    });
    setError("");
  };

  const submit = async event => {
    event.preventDefault();
    if (sending.current) return;
    setError("");
    let contact;
    try { contact = sampleContactPayload(form, form.country); }
    catch (err) {
      setError(err.message);
      return;
    }
    if (form.wants_extra && !form.sharing_with.length) {
      setError("Select who you’d like to share your extra bottles with.");
      return;
    }
    requestId.current ||= crypto.randomUUID();
    const payload = {
      request_id: requestId.current, name: form.name.trim(), ...contact,
      address: Object.fromEntries(["country", "address1", "address2", "city", "region", "postal_code"].map(key => [key, form[key].trim()])),
      additional_bottles: form.wants_extra ? Number(form.additional_bottles) : 0,
      sharing_with: form.wants_extra ? form.sharing_with : [],
      sharing_note: form.wants_extra && form.sharing_with.includes("Other") ? form.sharing_note.trim() : "",
    };
    sending.current = true;
    setSubmitting(true);
    try {
      await api.sampleInvite(payload);
      setSubmitted(true);
      toast.success("Your sample-kit request is in.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "string") setError(detail);
      else if (err.response?.status === 422) setError("Please check your contact details, mailing address, and bottle quantity. Required fields cannot be blank.");
      else setError("We couldn’t confirm your request. Please try again; your request won’t be duplicated.");
    } finally {
      sending.current = false;
      setSubmitting(false);
    }
  };

  return { form, setForm, update, submit, submitting, submitted, error };
};